import { useState } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { X, Zap, Battery, Moon } from 'lucide-react';

export default function ConvertToActivityModal({ idea, kidId, onClose }) {
  const { addActivity } = useFamilyContext();
  const [energyLevel, setEnergyLevel] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConvert = async () => {
    try {
      setLoading(true);
      setError('');
      
      const newActivity = {
        name: idea.title,
        category: mapIdeaCategoryToActivity(idea.category),
        description: idea.description,
        energyLevel: energyLevel,
        howToStart: idea.links && idea.links.length > 0 
          ? `Check out these resources: ${idea.links.join(', ')}`
          : null,
        isCustom: true,
        addedBy: 'user',
        links: idea.links || []
      };
      
      await addActivity(kidId, newActivity);
      onClose();
    } catch (err) {
      console.error('Error converting idea to activity:', err);
      setError('Failed to convert idea. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Map idea categories to activity categories
  const mapIdeaCategoryToActivity = (ideaCategory) => {
    const mapping = {
      'book': 'learning',
      'project': 'creative',
      'experience': 'social',
      'learning': 'learning'
    };
    return mapping[ideaCategory] || 'other';
  };

  const energyOptions = [
    { value: 'high', label: 'High Energy', icon: Zap, color: 'text-red-600', bgColor: 'bg-red-100', borderColor: 'border-red-500' },
    { value: 'medium', label: 'Medium Energy', icon: Battery, color: 'text-yellow-600', bgColor: 'bg-yellow-100', borderColor: 'border-yellow-500' },
    { value: 'low', label: 'Low Energy', icon: Moon, color: 'text-emerald_emerald-600', bgColor: 'bg-emerald_emerald-100', borderColor: 'border-emerald_emerald-500' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-50 rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-stone-900">Convert to Activity</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>

          <div className="mb-6">
            <h3 className="font-medium text-stone-900 mb-2">{idea.title}</h3>
            <p className="text-sm text-stone-600">{idea.description}</p>
            {idea.links && idea.links.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-stone-500">This activity will include {idea.links.length} resource link{idea.links.length > 1 ? 's' : ''}</p>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-stone-900 mb-3">
              Choose Energy Level
            </label>
            <div className="space-y-2">
              {energyOptions.map(option => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    onClick={() => setEnergyLevel(option.value)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                      energyLevel === option.value
                        ? `${option.bgColor} ${option.borderColor}`
                        : 'bg-neutral-50 border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${energyLevel === option.value ? option.color : 'text-stone-400'}`} />
                    <span className={`font-medium ${energyLevel === option.value ? option.color : 'text-stone-700'}`}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-stone-100 text-stone-700 font-medium rounded-lg hover:bg-stone-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConvert}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-indigo-600 text-neutral-50 font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Converting...' : 'Add to My Activities'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}